import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};

export const ThemeModeProvider = ({ children }) => {
  // Get initial theme from localStorage or default to 'light'
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('themeMode');
    if (savedTheme && ['light', 'dark', 'auto'].includes(savedTheme)) {
      return savedTheme;
    }
    return 'light';
  };

  const [themeMode, setThemeMode] = useState(getInitialTheme);

  // Function to get system preference
  const getSystemTheme = () => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  };

  // Calculate the actual theme (resolve 'auto' to 'light' or 'dark')
  const actualTheme = useMemo(() => {
    if (themeMode === 'auto') {
      return getSystemTheme();
    }
    return themeMode;
  }, [themeMode]);

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (themeMode === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = () => {
        // Force re-render by updating a state
        setThemeMode('auto');
      };

      // Add event listener for changes
      mediaQuery.addEventListener('change', handleChange);

      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
    return undefined;
  }, [themeMode]);

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  const changeThemeMode = (newMode) => {
    if (['light', 'dark', 'auto'].includes(newMode)) {
      setThemeMode(newMode);
    }
  };

  const value = {
    themeMode, // 'light', 'dark', or 'auto'
    actualTheme, // 'light' or 'dark' (auto resolved)
    changeThemeMode,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
